import { forwardRef, useRef, type PropsWithChildren, type RefObject } from "react";

import { observer } from "mobx-react-lite";

import { InlineNode } from "@/components/expression/InlineNode";
import { useNodePoints } from "@/components/expression/svgUtils/useNodePoints";
import {
  NodeStyleProvider,
  useNodeStyle,
  type Props as ProviderValue,
} from "@/components/expression/utils/NodeStyleProvider";
import { NumberNodeModel } from "@/models/nodes/NumberNodeModel";
import { twMerge } from "@/utils/twMerge";

import type { NodeRenderProps } from "@/components/expression/types";
import type { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import type { LabelNodePosition } from "@corca/data";

export type NodeAttachment = {
  node?: InlineNodeModel;
  nodeRenderProps?: NodeRenderProps;
  position: LabelNodePosition;
};

type AttachmentProps = {
  attachment?: NodeAttachment;
  className?: string;
  canOverlap?: boolean;
};

function canOverlapNode(attachment: NodeAttachment | undefined) {
  return !attachment?.node?.nodes[0]?.is(NumberNodeModel);
}

const Attachment = forwardRef<HTMLDivElement, AttachmentProps>(function Attachment(
  { attachment, className, canOverlap }: AttachmentProps,
  ref,
) {
  const { size, view } = useNodeStyle();
  const isTextView = view === "text";

  const sizesClassNames = twMerge(
    size === "sm" && "min-h-5.5",
    size === "xs" && "min-h-4",
    size === "xxs" && "min-h-3.5",
    isTextView && size === "xxs" && "min-h-2",
    isTextView && size === "xxxs" && "min-h-1",
  );

  const canOverlapOrDefault = canOverlap ?? canOverlapNode(attachment);

  return (
    <div
      ref={ref}
      className={twMerge(
        "flex flex-shrink-0 relative z-30",
        !isTextView && "min-w-2",
        sizesClassNames,
        !isTextView && !canOverlapOrDefault && "ml-0.75",
        className,
      )}
    >
      {attachment?.node && (
        <InlineNode node={attachment.node} nodeRenderProps={attachment.nodeRenderProps} />
      )}
    </div>
  );
});

type Props = {
  attachments: NodeAttachment[];
  position: "left" | "right";
  styleProviderValue?: ProviderValue;
  className?: string;
  topRef?: RefObject<HTMLDivElement | null>;
  bottomRef?: RefObject<HTMLDivElement | null>;
  canOverlap?: boolean;
};

export const NodeAttachments = observer(function NodeAttachments({
  attachments,
  position,
  styleProviderValue,
  className,
  topRef,
  bottomRef,
  canOverlap,
}: PropsWithChildren<Props>) {
  const { view } = useNodeStyle();
  const isTextView = (styleProviderValue?.view || view) === "text";

  const topAttachments = attachments.filter(
    (a) => a.node && a.position === (position === "left" ? "left-top" : "right-top"),
  );
  const bottomAttachments = attachments.filter(
    (a) => a.node && a.position === (position === "left" ? "left-bottom" : "right-bottom"),
  );

  if (topAttachments.length === 0 && bottomAttachments.length === 0) {
    return null;
  }

  return (
    <div className={twMerge("inline-flex flex-col flex-nowrap", className)}>
      <NodeStyleProvider {...styleProviderValue}>
        <Attachment
          ref={topRef}
          attachment={topAttachments[0]}
          className={twMerge("mb-auto items-end", !isTextView && "pb-[1px]")}
          canOverlap={canOverlap}
        />
        <Attachment
          ref={bottomRef}
          attachment={bottomAttachments[0]}
          className={twMerge("mt-auto items-start", !isTextView && "pt-[1px]")}
          canOverlap={canOverlap}
        />
      </NodeStyleProvider>
    </div>
  );
});

export function useAttachmentShift() {
  const nodeStyle = useNodeStyle();
  const isTextView = nodeStyle.view === "text";
  const bottomRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);
  const { viewBox: topViewBox } = useNodePoints(topRef);
  const { viewBox: bottomViewBox } = useNodePoints(bottomRef);

  return {
    refs: { topRef, bottomRef },
    shift: isTextView ? 0 : topViewBox.height - bottomViewBox.height,
  };
}
